import React, { useState, useEffect } from "react";
import axios from 'axios';
import EnhancedSequenceAlignmentViz from './testSequenceAlignment';
import { apiPath } from "../apiurl.js";
import LoadingSequence from "../assets/Loading/LoadingSequence";
import { UserGuideSequenceAlignment } from '../utils/UserGuide';
import { PaginationBar } from '../utils/PaginationBar';
import { useHistory } from 'react-router-dom';
import '../assets/css/SequenceAlignmentPage.css';

const SIMILARITY_RANGES = [
  { range: "> 95%",  min: 95, max: 100, color: "#10b981" },
  { range: "90-95%", min: 90, max: 95,  color: "#3b82f6" },
  { range: "85-90%", min: 85, max: 90,  color: "#06b6d4" },
  { range: "80-85%", min: 80, max: 85,  color: "#f59e0b" },
  { range: "75-80%", min: 75, max: 80,  color: "#f97316" },
  { range: "< 75%",  min: 0,  max: 75,  color: "#ef4444" },
];

const getSimilarityRange = (similarity) => {
  const pct = parseFloat(similarity);
  return SIMILARITY_RANGES.find(r => pct > r.min && pct <= r.max) || SIMILARITY_RANGES[SIMILARITY_RANGES.length - 1];
};

const ITEMS_PER_PAGE = 10;

function SequenceAlignmentPage() {
  const [fullData,            setFullData]            = useState([]);
  const [filteredData,        setFilteredData]        = useState([]);
  const [data,                setData]                = useState([]);
  const [isLoading,           setIsLoading]           = useState(false);
  const [noData,              setNoData]              = useState(false);
  const [run,                 setRun]                 = useState('SRR9686066');
  const [selectedDropdown,    setSelectedDropdown]    = useState('Read Hit');
  const [currentPage,         setCurrentPage]         = useState(1);
  const [selectedRangeFilter, setSelectedRangeFilter] = useState(null);
  const [analysisResults,     setAnalysisResults]     = useState([]);
  const [subjectSearch,       setSubjectSearch]       = useState('');
  const [selectedAlignment,   setSelectedAlignment]   = useState(null);
  const [selectedAlignmentIndex, setSelectedAlignmentIndex] = useState(null);
  const [userGuideOpen,       setUserGuideOpen]       = useState(false);
  const history = useHistory();

  useEffect(() => { searchApi(); }, [run, selectedDropdown]);

  useEffect(() => {
    if (fullData.length === 0) return;
    const searchTerm = subjectSearch.toLowerCase();
    const filtered = subjectSearch
      ? fullData.filter(item => item.sseqid?.toLowerCase().includes(searchTerm))
      : fullData;
    setFilteredData(filtered);
    applyRangeFilter(filtered, selectedRangeFilter);
  }, [subjectSearch, fullData]);

  const applyRangeFilter = (dataset, range) => {
    let results = range
      ? dataset.filter(item => {
          const idx = fullData.indexOf(item);
          const res = analysisResults.find(r => r.alignmentIndex === idx);
          if (!res) return false;
          return res.similarity > range.min && res.similarity <= range.max;
        })
      : dataset;
    setData(results.slice(0, ITEMS_PER_PAGE));
    setCurrentPage(1);
    if (results.length > 0 && (!selectedAlignment || !results.includes(selectedAlignment))) {
      setSelectedAlignment(results[0]);
      setSelectedAlignmentIndex(fullData.indexOf(results[0]));
    } else if (results.length === 0) {
      setSelectedAlignment(null);
      setSelectedAlignmentIndex(null);
    }
  };

  const searchApi = () => {
    setIsLoading(true);
    setNoData(false);
    setSelectedAlignment(null);
    setSelectedAlignmentIndex(null);
    setAnalysisResults([]);
    const endpoint = selectedDropdown === 'Read Hit' ? 'readhit' : 'contighits';
    const url = run ? `${apiPath}/api/${endpoint}/${run}` : '';

    axios.get(url)
      .then(({ data: res }) => {
        setFullData(res);
        setFilteredData(res);
        setSelectedRangeFilter(null);
        setSubjectSearch('');
        setData(res.slice(0, ITEMS_PER_PAGE));
        if (res.length > 0) {
          setSelectedAlignment(res[0]);
          setSelectedAlignmentIndex(0);
        }
        setIsLoading(false);
        setNoData(res.length === 0);
      })
      .catch(() => { setIsLoading(false); setNoData(true); });
  };

  const addAnalysisResult = (alignmentIndex, info) => {
    setAnalysisResults(prev => {
      const idx = prev.findIndex(r => r.alignmentIndex === alignmentIndex);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...info };
        return updated;
      }
      return [...prev, { alignmentIndex, ...info }];
    });
  };

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const offset = (page - 1) * ITEMS_PER_PAGE;
    let dataset = filteredData;
    if (selectedRangeFilter) {
      const { min, max } = selectedRangeFilter;
      dataset = filteredData.filter(item => {
        const idx = fullData.indexOf(item);
        const res = analysisResults.find(r => r.alignmentIndex === idx);
        return res && res.similarity > min && res.similarity <= max;
      });
    }
    setData(dataset.slice(offset, offset + ITEMS_PER_PAGE));
  };

  const handleRangeChip = (range) => {
    if (selectedRangeFilter?.range === range.range) {
      setSelectedRangeFilter(null);
      setData(filteredData.slice(0, ITEMS_PER_PAGE));
      setCurrentPage(1);
      if (filteredData.length > 0) {
        setSelectedAlignment(filteredData[0]);
        setSelectedAlignmentIndex(fullData.indexOf(filteredData[0]));
      }
      return;
    }
    setSelectedRangeFilter(range);
    applyRangeFilter(filteredData, range);
  };

  const handleClear = () => {
    setRun('');
    setSelectedDropdown('Read Hit');
    setData([]);
    setNoData(false);
    setIsLoading(false);
    setAnalysisResults([]);
    setSelectedRangeFilter(null);
    setSelectedAlignment(null);
    setSelectedAlignmentIndex(null);
    setSubjectSearch('');
    setFilteredData([]);
    window.location.reload();
  };

  const handleRouteClick = () => {
    const endpoint = selectedDropdown === 'Contig Hits' ? 'ContigHits' : 'ReadHits';
    history.push(`/pass/${endpoint}?run=${run}`);
  };

  const rangeCounts = {};
  SIMILARITY_RANGES.forEach(r => {
    rangeCounts[r.range] = analysisResults.filter(res =>
      res.similarity > r.min && res.similarity <= r.max
    ).length;
  });

  const renderList = () => {
    if (isLoading) return <LoadingSequence message="Loading alignments" size="medium" />;
    if (noData || data.length === 0) {
      return (
        <div className="sap-list-empty">
          {subjectSearch || selectedRangeFilter
            ? <>No results match your filters.<br />
                <button className="sap-chip-clear mt-2" onClick={() => { setSubjectSearch(''); setSelectedRangeFilter(null); setFilteredData(fullData); setData(fullData.slice(0, ITEMS_PER_PAGE)); }}>
                  Clear filters
                </button>
              </>
            : 'Enter a Run ID and click Search to load alignments.'}
        </div>
      );
    }

    return data.map((alignment, i) => {
      const fullIdx = fullData.indexOf(alignment);
      const res = analysisResults.find(r => r.alignmentIndex === fullIdx);
      const simRange = res ? getSimilarityRange(res.similarity) : null;
      const itemNum = i + 1 + (currentPage - 1) * ITEMS_PER_PAGE;

      return (
        <div
          key={i}
          className={`sap-item${selectedAlignment === alignment ? ' active' : ''}`}
          onClick={() => { setSelectedAlignment(alignment); setSelectedAlignmentIndex(fullIdx); }}
        >
          <div className="sap-item-top">
            <span className="sap-item-num">Alignment {itemNum}</span>
            {res && (
              <span className="sap-sim-badge" style={{ background: simRange.color }}>
                {res.similarity.toFixed(2)}%
              </span>
            )}
          </div>
          <div className="sap-item-id"><b>Q:</b> {alignment.qseqid || '—'}</div>
          <div className="sap-item-id"><b>S:</b> {alignment.sseqid || '—'}</div>
          {res && (
            <div className="sap-item-ft">
              <span>{simRange.range}</span>
              {res.bestMatch && (
                <span className="sap-best">✓ {res.bestMatch}</span>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      <div className="sap-root">
        {/* ── Toolbar ── */}
        <div className="sap-toolbar">
          <span className="sap-toolbar-hint">Run:</span>
          <input
            className="sap-run-input"
            type="text"
            placeholder="e.g. SRR9686066"
            value={run}
            onChange={e => setRun(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchApi()}
          />
          <select
            className="sap-type-select"
            value={selectedDropdown}
            onChange={e => setSelectedDropdown(e.target.value)}
          >
            <option value="Read Hit">Read Hit</option>
            <option value="Contig Hits">Contig Hits</option>
          </select>
          <button className="sap-btn sap-btn-search" onClick={searchApi} disabled={isLoading}>
            {isLoading ? '…' : '⌕ Search'}
          </button>
          <button className="sap-btn sap-btn-clear" onClick={handleClear}>
            ✕ Clear
          </button>
          <button className="sap-btn sap-btn-export" onClick={() => {}}>
            ↓ Export PDF
          </button>
          <button className="sap-btn sap-btn-guide" onClick={() => setUserGuideOpen(true)}>
            ? Guide
          </button>
        </div>

        {/* ── Body ── */}
        <div className="sap-body">
          {/* Left sidebar */}
          <div className="sap-sidebar">
            <div className="sap-sidebar-hd">
              <div className="sap-sidebar-label">Alignments ({filteredData.length})</div>

              <div className="sap-subject-row">
                <input
                  className="sap-subject-input"
                  placeholder="Filter by Subject ID…"
                  value={subjectSearch}
                  onChange={e => setSubjectSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (() => {
                    const term = subjectSearch.toLowerCase();
                    const filtered = term ? fullData.filter(d => d.sseqid?.toLowerCase().includes(term)) : fullData;
                    setFilteredData(filtered);
                    applyRangeFilter(filtered, selectedRangeFilter);
                  })()}
                />
                <button
                  className="sap-icon-btn"
                  onClick={() => {
                    const term = subjectSearch.toLowerCase();
                    const filtered = term ? fullData.filter(d => d.sseqid?.toLowerCase().includes(term)) : fullData;
                    setFilteredData(filtered);
                    applyRangeFilter(filtered, selectedRangeFilter);
                  }}
                >
                  ⌕
                </button>
              </div>

              {analysisResults.length > 0 && (
                <>
                  <div className="sap-filter-hd">
                    <span className="sap-filter-hd-label">Filter by Similarity</span>
                    <span className="sap-filter-count">{analysisResults.length} analyzed</span>
                  </div>
                  <div className="sap-chips">
                    {SIMILARITY_RANGES.map(range => {
                      const count = rangeCounts[range.range] || 0;
                      if (!count) return null;
                      const isActive = selectedRangeFilter?.range === range.range;
                      return (
                        <button
                          key={range.range}
                          className={`sap-chip${isActive ? ' active' : ''}`}
                          style={{ color: isActive ? '#fff' : range.color, borderColor: range.color, background: isActive ? range.color : 'transparent' }}
                          onClick={() => handleRangeChip(range)}
                        >
                          {range.range} ({count})
                        </button>
                      );
                    })}
                    {selectedRangeFilter && (
                      <button className="sap-chip-clear" onClick={() => handleRangeChip(selectedRangeFilter)}>
                        ✕ Clear
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="sap-list">
              {renderList()}
            </div>
          </div>

          {/* Main visualization panel */}
          <div className="sap-main">
            <div className="sap-viz-hd">
              <div className="sap-viz-hd-title">Sequence Alignment Visualization</div>
              {selectedAlignment
                ? <div className="sap-viz-hd-sub">
                    Query: {selectedAlignment.qseqid} · Subject: {selectedAlignment.sseqid}
                  </div>
                : <div className="sap-viz-hd-sub" style={{ color: '#94a3b8' }}>
                    Select an alignment from the list
                  </div>
              }
            </div>

            <div className="sap-viz-body">
              {isLoading ? (
                <LoadingSequence message="Loading data" size="medium" />
              ) : selectedAlignment ? (
                <EnhancedSequenceAlignmentViz
                  alignments={selectedAlignment}
                  onSimilarityRangeFound={matchInfo => {
                    if (selectedAlignmentIndex !== null) {
                      addAnalysisResult(selectedAlignmentIndex, {
                        similarity: matchInfo.similarity,
                        range: matchInfo.range,
                        bestMatch: matchInfo.strain,
                        positions: [`${matchInfo.position} – ${parseInt(matchInfo.position) + (selectedAlignment.qseq?.length || 0)}`],
                      });
                    }
                  }}
                />
              ) : (
                <div className="sap-viz-empty">
                  <span className="sap-viz-empty-icon">🧬</span>
                  <span>Select an alignment from the list to view the visualization</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="sap-footer">
          <div className="sap-footer-left">
            {/* placeholder to keep layout balanced */}
          </div>
          <div className="sap-footer-center">
            <PaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
            <span>Page {currentPage} of {totalPages || 1}</span>
          </div>
          <div className="sap-footer-right">
            {run && (
              <button className="sap-btn sap-btn-datatable" onClick={handleRouteClick}>
                View {run} Data Table →
              </button>
            )}
          </div>
        </div>
      </div>

      <UserGuideSequenceAlignment open={userGuideOpen} onClose={() => setUserGuideOpen(false)} />
    </>
  );
}

export default SequenceAlignmentPage;
